import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

export const PublicFooter: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const footerSections = [
    {
      title: 'For Patients',
      links: [
        { label: 'Find Caregivers', href: '/find-caregivers' },
        { label: 'How It Works', href: '/how-it-works' },
        { label: 'Safety & Trust', href: '/safety' },
        { label: 'Insurance Coverage', href: '/insurance' },
        { label: 'Patient Stories', href: '/stories' },
      ],
    },
    {
      title: 'For Caregivers',
      links: [
        { label: 'Become a Caregiver', href: '/auth?role=caregiver' },
        { label: 'Caregiver Resources', href: '/caregiver-resources' },
        { label: 'Earning Potential', href: '/earnings' },
        { label: 'Background Check', href: '/background-check' },
        { label: 'Caregiver Stories', href: '/caregiver-stories' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Our Mission', href: '/mission' },
        { label: 'Careers', href: '/careers' },
        { label: 'Press', href: '/press' },
        { label: 'Contact Us', href: '/contact' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '/help' },
        { label: 'Safety Guidelines', href: '/safety-guidelines' },
        { label: 'Community Guidelines', href: '/community-guidelines' },
        { label: 'Report an Issue', href: '/report' },
        { label: 'Accessibility', href: '/accessibility' },
      ],
    },
  ];

  const legalLinks = [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Cookie Policy', href: '/cookies' },
    { label: 'HIPAA Compliance', href: '/hipaa' },
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, href: 'https://facebook.com/careva', label: 'Facebook' },
    { icon: <TwitterIcon />, href: 'https://twitter.com/careva', label: 'Twitter' },
    { icon: <LinkedInIcon />, href: 'https://linkedin.com/company/careva', label: 'LinkedIn' },
    { icon: <InstagramIcon />, href: 'https://instagram.com/careva', label: 'Instagram' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.grey[900],
        color: 'white',
        py: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.light,
                  mb: 2,
                }}
              >
                Careva
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.grey[300],
                  lineHeight: 1.6,
                  mb: 3,
                }}
              >
                Connecting patients with qualified, verified caregivers for 
                physical therapy, occupational therapy, senior care, and home 
                health services. Quality care, when and where you need it.
              </Typography>
              
              {/* Newsletter Signup */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'white',
                    mb: 1,
                    fontWeight: 'bold',
                  }}
                >
                  Stay Updated
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box
                    component="input"
                    placeholder="Enter your email"
                    sx={{
                      flex: 1,
                      p: 1,
                      border: '1px solid',
                      borderColor: theme.palette.grey[600],
                      borderRadius: 1,
                      backgroundColor: theme.palette.grey[800],
                      color: 'white',
                      fontSize: '0.875rem',
                      '&::placeholder': {
                        color: theme.palette.grey[400],
                      },
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  />
                  <Box
                    component="button"
                    sx={{
                      p: 1,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      border: 'none',
                      borderRadius: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    <EmailIcon fontSize="small" />
                  </Box>
                </Box>
              </Box>

              {/* Social Links */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'white',
                    mb: 1,
                    fontWeight: 'bold',
                  }}
                >
                  Follow Us
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: theme.palette.grey[300],
                        '&:hover': {
                          color: theme.palette.primary.light,
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      aria-label={social.label}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <Grid item xs={6} md={2} key={index}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link, linkIndex) => (
                  <Box component="li" key={linkIndex} sx={{ mb: 1 }}>
                    <Link
                      component={RouterLink}
                      to={link.href}
                      sx={{
                        color: theme.palette.grey[300],
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                          color: theme.palette.primary.light,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: theme.palette.grey[700] }} />

        {/* Bottom Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          {/* Copyright */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[400],
            }}
          >
            © {new Date().getFullYear()} Careva. All rights reserved.
          </Typography>

          {/* Legal Links */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {legalLinks.map((link, index) => (
              <Link
                key={index}
                component={RouterLink}
                to={link.href}
                sx={{
                  color: theme.palette.grey[400],
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: theme.palette.primary.light,
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        </Box>

        {/* Trust Badges */}
        <Box
          sx={{
            mt: 4,
            pt: 4,
            borderTop: `1px solid ${theme.palette.grey[700]}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[400],
              mb: 2,
            }}
          >
            Trusted and Secure
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[300],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              ✓ HIPAA Compliant
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[300],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              ✓ SSL Encrypted
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[300],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              ✓ Background Checked
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[300],
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              ✓ Insured & Bonded
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
